<script lang="ts">
  import { LAYER, LAYER_LABEL, LAYER_ORDER, GLOSS, type Chapter, type Entry } from '../data/grimoire';
  import { store } from '../store.svelte';
  import { visited } from '../visited.svelte';
  import { reveal } from '../actions';
  import Node from './Node.svelte';

  const TIER_LABEL: Record<string, string> = {
    A: 'Тир A · доказано, но как магия',
    B: 'Тир B · фронтир · правдоподобно и тонко',
    C: 'Тир C · изучали и не нашли',
  };

  let { chapter }: { chapter: Chapter } = $props();

  type Row =
    | { kind: 'label'; text: string }
    | { kind: 'entry'; entry: Entry };

  const rows = $derived.by<Row[]>(() => {
    const r = chapter;
    const out: Row[] = [];
    let lastGroup: string | null = null;

    let entries = r.entries;
    if (r.id === 'reg-1') {
      entries = [...r.entries].sort(
        (a, b) => (LAYER_ORDER[LAYER[a.id]] ?? 9) - (LAYER_ORDER[LAYER[b.id]] ?? 9),
      );
    }

    for (const e of entries) {
      if (r.id === 'reg-2' && e.tier && e.tier !== lastGroup) {
        out.push({ kind: 'label', text: TIER_LABEL[e.tier] });
        lastGroup = e.tier;
      } else if (r.id === 'reg-1') {
        const lg = LAYER[e.id];
        if (lg && lg !== lastGroup) {
          out.push({ kind: 'label', text: LAYER_LABEL[lg] || lg });
          lastGroup = lg;
        }
      }
      out.push({ kind: 'entry', entry: e });
    }
    return out;
  });

  const filtering = $derived(store.query.trim() !== '' || store.trainOnly);
  const querying = $derived(store.query.trim() !== '');
  const chapterVisible = $derived(store.isChapterVisible(chapter.id));

  // Per-chapter exploration progress. Reading visited.size forces the rune
  // to re-derive whenever a new entry is marked.
  const progress = $derived.by(() => {
    void visited.size;
    const total = chapter.entries.length;
    const seen = chapter.entries.filter((e) => visited.has(e.id)).length;
    return { seen, total, pct: total ? seen / total : 0 };
  });
</script>

<section
  class="chapter"
  id={chapter.id}
  style:--accent={chapter.accent}
  style:display={chapterVisible ? '' : 'none'}
>
  <div class="wrap reveal" use:reveal>
    <div class="chapter__head">
      <div class="chapter__roman">{chapter.roman}</div>
      <div class="chapter__meta">
        <div class="chapter__meta-top">
          <div class="mono stage">{chapter.stage} · {GLOSS[chapter.id]}</div>
          <div
            class="chapter__progress"
            title="{progress.seen} из {progress.total} раскрыто"
            aria-label="{progress.seen} из {progress.total} раскрыто"
          >
            <span class="chapter__progress-num">{progress.seen}/{progress.total}</span>
            <span class="chapter__progress-bar" style:--pct={progress.pct}></span>
          </div>
        </div>
        <h2 class="chapter__title">{chapter.title}</h2>
        <p class="chapter__law">{@html chapter.law}</p>
      </div>
    </div>
    <div class="tiergrid">
      {#each rows as row, i (row.kind === 'entry' ? `e:${row.entry.id}` : `l:${i}:${row.text}`)}
        {#if row.kind === 'label'}
          <div class="tierlabel" style:display={filtering ? 'none' : ''}>
            <span>{row.text}</span><hr />
          </div>
        {:else}
          <Node entry={row.entry} />
        {/if}
      {/each}
    </div>
    {#if chapter.synth}
      <aside class="synth" style:display={querying ? 'none' : ''}>
        <div class="synth__t">{chapter.synth.t}</div>
        <dl>
          {#each chapter.synth.items as item (item.l)}
            <div><dt>{item.l}</dt><dd>{item.v}</dd></div>
          {/each}
        </dl>
      </aside>
    {/if}
  </div>
</section>

<style>
  .chapter__meta-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
    flex-wrap: wrap;
  }
  .chapter__progress {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-family: var(--mono);
    font-size: 0.56rem;
    letter-spacing: 0.12em;
    color: var(--ink-faint);
  }
  .chapter__progress-num {
    white-space: nowrap;
  }
  .chapter__progress-bar {
    display: inline-block;
    width: 64px;
    height: 2px;
    background: var(--hair-2);
    position: relative;
    border-radius: 1px;
  }
  .chapter__progress-bar::after {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: calc(var(--pct, 0) * 100%);
    background: var(--accent);
    border-radius: 1px;
    transition: width 0.4s ease;
  }
  @media (max-width: 640px) {
    .chapter__progress-bar {
      width: 44px;
    }
  }
</style>
